/**
 * Chunk-load errors happen when a user has an old page open and a fresh
 * deploy invalidates the hashed chunk filenames it references. The fix is
 * always the same: reload so the browser fetches the new HTML (which points
 * at the new chunks).
 *
 * We detect both runtimes' error shapes:
 *   - webpack: `ChunkLoadError` name + "Loading chunk N failed" / "Loading
 *     CSS chunk N failed" message.
 *   - turbopack (Next 16 default): "Failed to load chunk <path> from module
 *     <id>" message; no special name.
 *
 * Auto-reload is guarded by a sessionStorage flag so a chunk that is
 * genuinely missing on the server doesn't trap the user in a reload loop.
 */

const RELOAD_FLAG = "railyard_chunk_reload_attempted";

const CHUNK_MESSAGE_PATTERNS: readonly RegExp[] = [
  /Loading chunk [^\s]+ failed/i,
  /Loading CSS chunk [^\s]+ failed/i,
  /Failed to load chunk/i,
  /ChunkLoadError/i,
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: unknown; message?: unknown };
  if (typeof err.name === "string" && err.name === "ChunkLoadError") return true;
  const message =
    typeof err.message === "string"
      ? err.message
      : typeof error === "string"
        ? error
        : "";
  if (!message) return false;
  return CHUNK_MESSAGE_PATTERNS.some((re) => re.test(message));
}

/**
 * True if we haven't yet auto-reloaded this session. Read-only — safe to
 * call during render to decide which UI to show.
 */
export function shouldAttemptChunkReload(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return !window.sessionStorage.getItem(RELOAD_FLAG);
  } catch {
    // sessionStorage can throw in private mode / disabled cookies; without
    // dedupe we can't safely auto-reload.
    return false;
  }
}

/**
 * Mark the session as having attempted a reload and trigger it. Pair with
 * `shouldAttemptChunkReload()` to avoid loops when the chunk is genuinely
 * missing from the deploy.
 */
export function triggerChunkReload(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    // If we can't set the flag, skip the reload — better to surface the
    // error than to risk a loop.
    return;
  }
  window.location.reload();
}
